import Button from '@shared/components/Button/Button';
import { ClientEvent, KnownMembership, MatrixError, Room, RoomMember, SyncState } from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { getMeetingByRoomId } from 'api/meedioBackend/getMeetingByRoomId';
import { MeetingResponse } from 'api/meedioBackend/types';
import { ReactComponent as UserPlus } from 'assets/icons/UserPlus.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import { useMatrixShellReadyState } from 'modules/Matrix/isMatrixShellStateReady';
import { findUserInviteMemberEvent } from 'modules/Notifications/deferredInviteSnapshot';
import { getMeetingSpaceInviteNotificationId } from 'modules/Notifications/inviteNotificationIds';
import { isVideoHubScheduledMeetingInvite } from 'modules/Notifications/inviteNotificationRoutes';
import {
  isVideoHubGuestMeeting,
  resolveMeetingWorkspaceTarget,
  shouldSurfaceMeetingSpaceInvite,
} from 'modules/Notifications/meetingSpaceInviteUtils';
import useNotifications from 'modules/Notifications/NotificationsProvider/useNotifications';
import useDeferredInviteGate from 'modules/Notifications/useDeferredInviteGate';
import useDeferredInviteSnapshot from 'modules/Notifications/useDeferredInviteSnapshot';
import useInviteDeferFlush from 'modules/Notifications/useInviteDeferFlush';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';

import { useDeferInviteSession } from './DeferInviteSessionProvider';
import { inviteFlushDebug } from './inviteFlushDebug';
import { dismissInviteNotification } from './inviteNotificationDismiss';
import { shouldProcessInviteNotifications } from './shouldProcessInviteNotifications';

const RECENT_INVITE_THRESHOLD = 1000 * 10;

const useMeetingSpaceInviteNotifications = () => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const { workspacesState, backendGuestMeetings: contextGuestMeetings } = useWorkspacesContext();
  const deferSession = useDeferInviteSession();
  const { addNotification, removeNotification, markAsRead, isNotificationRead } = useNotifications();
  const { pushToast, pushNotificationToast, removeToast, isToastPresent, editNotificationToast } = useToast();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const shownInvitesRef = useRef<Set<string>>(new Set());
  const flushPendingRef = useRef(false);
  const isMatrixReady = useMatrixShellReadyState(matrixClient);

  const clearDeferredUi = useCallback(() => {
    [...shownInvitesRef.current].forEach((roomId) => {
      const notificationId = getMeetingSpaceInviteNotificationId(roomId);
      removeNotification(notificationId);
      removeToast(notificationId);
      shownInvitesRef.current.delete(roomId);
    });
  }, [removeNotification, removeToast]);

  const { isDeferred, isDeferredRef } = useDeferredInviteGate(clearDeferredUi);
  const deferredSnapshot = useDeferredInviteSnapshot(
    isDeferred,
    (member: RoomMember) => {
      if (member.membership !== KnownMembership.Invite) return false;
      const room = matrixClient.getRoom(member.roomId);
      return !!room && isVideoHubScheduledMeetingInvite(room);
    },
    undefined,
    (roomId) => deferSession.isRoomInBaseline(roomId)
  );

  const isLiveMeetingSpaceInvite = (room: Room): boolean =>
    room.getMyMembership() === KnownMembership.Invite && isVideoHubScheduledMeetingInvite(room);

  const processInvites = useCallback(async () => {
    const myUserId = matrixClient.getUserId();
    if (!myUserId) return;

    const currentInvites = shownInvitesRef.current;

    const removeMeetingSpaceInviteUi = (roomId: string) => {
      const notificationId = getMeetingSpaceInviteNotificationId(roomId);
      removeNotification(notificationId);
      removeToast(notificationId);
      currentInvites.delete(roomId);
    };

    if (isDeferredRef.current) {
      [...currentInvites].forEach(removeMeetingSpaceInviteUi);
      return;
    }

    const isFlush = flushPendingRef.current && deferSession.deferStartedAt !== null;
    flushPendingRef.current = false;

    const flushedSnapshots = isFlush ? deferredSnapshot.drain() : [];
    const eligibleSnapshots = flushedSnapshots.filter((snapshot) =>
      deferSession.isEligibleForFlush({
        notificationId: getMeetingSpaceInviteNotificationId(snapshot.roomId),
        inviteTimestamp: snapshot.inviteTimestamp,
        trackedAt: snapshot.trackedAt,
        isNotificationRead,
      })
    );
    const snapshotByRoomId = new Map(eligibleSnapshots.map((snapshot) => [snapshot.roomId, snapshot]));

    const backendGuestMeetings = [...contextGuestMeetings];

    const deferStart = deferSession.deferStartedAt;
    const filteredGuestMeetings = deferStart
      ? backendGuestMeetings.filter((m) => Date.parse(m.created_at) >= deferStart)
      : isFlush
      ? []
      : backendGuestMeetings;
    const backendGuestByRoomId = new Map(filteredGuestMeetings.map((meeting) => [meeting.matrix_room_id, meeting]));

    const candidateRoomIds = new Set<string>([
      ...matrixClient
        .getRooms()
        .filter(isLiveMeetingSpaceInvite)
        .filter((room) => !isFlush || !deferSession.isRoomInBaseline(room.roomId))
        .map((room) => room.roomId),
      ...eligibleSnapshots.map((snapshot) => snapshot.roomId),
      ...filteredGuestMeetings.map((meeting) => meeting.matrix_room_id),
    ]);

    if (isFlush) {
      inviteFlushDebug('MEET-FLUSH-DEBUG', {
        isFlush,
        flushedCount: flushedSnapshots.length,
        eligibleCount: eligibleSnapshots.length,
        candidateRoomIds: [...candidateRoomIds],
        backendGuestCount: filteredGuestMeetings.length,
        deferStart,
        sessionActive: deferSession.isActive,
      });
    }

    const eligibleEntries = new Map<string, MeetingResponse | null>();

    await Promise.all(
      [...candidateRoomIds].map(async (roomId) => {
        const backendGuest = backendGuestByRoomId.get(roomId);
        const room = matrixClient.getRoom(roomId);
        const membership = room?.getMyMembership();
        const wasDeferredInvite = snapshotByRoomId.has(roomId);
        const isBackendGuest = backendGuest != null;

        if (!shouldSurfaceMeetingSpaceInvite({ membership, wasDeferredInvite, isBackendGuest })) {
          if (isFlush) {
            inviteFlushDebug('MEET-FLUSH-DEBUG', {
              step: 'skip',
              roomId,
              reason: 'shouldSurfaceMeetingSpaceInvite false',
              membership,
              wasDeferredInvite,
              isBackendGuest,
            });
          }
          return;
        }
        if (!isBackendGuest && (!room || !isVideoHubScheduledMeetingInvite(room))) {
          if (isFlush) {
            inviteFlushDebug('MEET-FLUSH-DEBUG', {
              step: 'skip',
              roomId,
              reason: 'not video hub scheduled meeting invite',
              hasRoom: !!room,
            });
          }
          return;
        }

        if (backendGuest) {
          eligibleEntries.set(roomId, backendGuest);
          return;
        }

        try {
          const meeting = await getMeetingByRoomId(roomId);
          if (!meeting) {
            eligibleEntries.set(roomId, null);
            return;
          }
          if (meeting.cancelled_at || meeting.messenger_room_id != null || !meeting.scheduled_at) {
            if (isFlush) inviteFlushDebug('MEET-FLUSH-DEBUG', { step: 'skip', roomId, reason: 'meeting filtered out' });
            return;
          }
          if (!isVideoHubGuestMeeting(meeting, myUserId)) {
            if (isFlush) inviteFlushDebug('MEET-FLUSH-DEBUG', { step: 'skip', roomId, reason: 'not guest meeting' });
            return;
          }
          eligibleEntries.set(roomId, meeting);
        } catch (error) {
          logger.warn(`Error checking backend meeting for room ${roomId}, showing notification anyway`, error);
          eligibleEntries.set(roomId, null);
        }
      })
    );

    if (isDeferredRef.current) {
      [...currentInvites].forEach(removeMeetingSpaceInviteUi);
      return;
    }

    currentInvites.forEach((roomId) => {
      if (!eligibleEntries.has(roomId)) removeMeetingSpaceInviteUi(roomId);
    });

    for (const [roomId, meeting] of eligibleEntries) {
      if (isDeferredRef.current) break;

      const room = matrixClient.getRoom(roomId);
      const backendGuest = backendGuestByRoomId.get(roomId);
      const inviteEvent = room ? findUserInviteMemberEvent(room, myUserId) : undefined;
      const snapshot = snapshotByRoomId.get(roomId);

      const notificationId = getMeetingSpaceInviteNotificationId(roomId);
      const inviterId =
        inviteEvent?.getSender() ?? snapshot?.inviterId ?? meeting?.created_by_mxid ?? backendGuest?.created_by_mxid;
      const inviteTimestamp =
        inviteEvent?.getTs() ??
        snapshot?.inviteTimestamp ??
        (meeting?.created_at ? Date.parse(meeting.created_at) : undefined);
      const notificationTimestamp =
        typeof inviteTimestamp === 'number' && inviteTimestamp > 0 ? inviteTimestamp : Date.now();
      const isRecentInvite =
        !!inviteTimestamp && inviteTimestamp > 0 && Date.now() - inviteTimestamp < RECENT_INVITE_THRESHOLD;

      const inviterDisplayName =
        matrixClient.getUser(inviterId || '')?.displayName || inviterId?.split(':')[0]?.replace('@', '');

      const roomName = room?.name ?? meeting?.room_name ?? backendGuest?.room_name ?? roomId;

      const inviteTitle = (
        <Trans
          i18nKey="video_hub.meeting_invite_received"
          components={{ bold: <span className="font-medium" /> }}
          values={{ inviterName: inviterDisplayName, roomName }}
        />
      );

      const meetingDestination = `/meetings/${roomId}`;
      const meetingForWorkspace = meeting ?? backendGuest ?? null;

      const openMeetingDetails = () => {
        const targetWorkspaceId = resolveMeetingWorkspaceTarget(
          meetingForWorkspace,
          matrixClient,
          roomId,
          workspacesState.workspaces
        );
        navigate(meetingDestination, {
          state: {
            ...(targetWorkspaceId !== undefined && { workspaceId: targetWorkspaceId }),
            fromMeetingInvite: true,
          },
        });
      };

      const handleJoin = async () => {
        try {
          await matrixClient.joinRoom(roomId);
        } catch (error) {
          if (!(error instanceof MatrixError && error.errcode === 'M_ALREADY_MEMBER')) {
            pushToast({ variant: 'error', title: t('errors.something_went_wrong') });
            return;
          }
        }

        openMeetingDetails();
        removeMeetingSpaceInviteUi(roomId);
      };

      const membership = room?.getMyMembership();
      const canShowDecline = backendGuest == null && membership === KnownMembership.Invite;

      const handleDecline = () => {
        dismissInviteNotification({
          kind: 'decline',
          notificationId,
          roomId,
          markAsRead,
          removeNotification,
          removeToast,
          matrixClient: canShowDecline ? matrixClient : undefined,
        });
        currentInvites.delete(roomId);
      };

      if (currentInvites.has(roomId)) {
        if (isFlush) {
          inviteFlushDebug('MEET-FLUSH-DEBUG', { step: 'skip', roomId, reason: 'already in currentInvites' });
        }
        if (isToastPresent(notificationId)) {
          editNotificationToast(notificationId, {
            id: notificationId,
            icon: <UserPlus className="h-5 w-5 stroke-primary-70 stroke-1.5" />,
            title: inviteTitle,
            onPrimaryAction: handleJoin,
            primaryActionText: t('join'),
            ...(canShowDecline && { secondaryActionText: t('decline'), onSecondaryAction: handleDecline }),
            onClose: () => {
              dismissInviteNotification({
                kind: 'close',
                notificationId,
                roomId,
                markAsRead,
                removeNotification,
                removeToast,
              });
            },
          });
        }
        continue;
      }

      if (isFlush) inviteFlushDebug('MEET-FLUSH-DEBUG', { step: 'add', roomId, roomName, isRecentInvite });

      if (isDeferredRef.current) continue;

      addNotification({
        id: notificationId,
        icon: UserPlus,
        variant: 'primary',
        title: inviteTitle,
        content: (
          <div className="flex justify-end gap-2 pt-2">
            {canShowDecline && (
              <Button
                variant="tertiary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDecline();
                }}
              >
                <Trans i18nKey="decline" />
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleJoin();
              }}
            >
              <Trans i18nKey="join" />
            </Button>
          </div>
        ),
        isRead: false,
        timestamp: notificationTimestamp,
        onClick: openMeetingDetails,
      });

      logger.info(`Added meeting space invite notification for room ${roomId} from ${inviterId || 'unknown'}`);

      if (isRecentInvite || isFlush) {
        if (isDeferredRef.current) continue;

        pushNotificationToast({
          id: notificationId,
          icon: <UserPlus className="h-5 w-5 stroke-primary-70 stroke-1.5" />,
          title: inviteTitle,
          onPrimaryAction: handleJoin,
          primaryActionText: t('join'),
          ...(canShowDecline && { secondaryActionText: t('decline'), onSecondaryAction: handleDecline }),
          onClose: () => {
            dismissInviteNotification({
              kind: 'close',
              notificationId,
              roomId,
              markAsRead,
              removeNotification,
              removeToast,
            });
          },
        });

        logger.info(`Displayed meeting space invite toast for room ${roomId} from ${inviterId || 'unknown'}`);
      }

      currentInvites.add(roomId);
    }
  }, [
    matrixClient,
    addNotification,
    removeNotification,
    pushToast,
    pushNotificationToast,
    removeToast,
    isToastPresent,
    editNotificationToast,
    navigate,
    t,
    deferredSnapshot,
    contextGuestMeetings,
    workspacesState.workspaces,
    deferSession,
    markAsRead,
    isNotificationRead,
    isDeferredRef,
  ]);

  useInviteDeferFlush(isDeferred, () => {
    flushPendingRef.current = true;
  });

  useEffect(() => {
    const processInvitesIfReady = () => {
      if (!shouldProcessInviteNotifications(matrixClient)) return;
      void processInvites();
    };

    const handleSync = (state: SyncState) => {
      if (state === SyncState.Syncing) processInvitesIfReady();
    };

    matrixClient.on(ClientEvent.Sync, handleSync);
    matrixClient.on(ClientEvent.Room, processInvitesIfReady);

    processInvitesIfReady();

    return () => {
      matrixClient.off(ClientEvent.Sync, handleSync);
      matrixClient.off(ClientEvent.Room, processInvitesIfReady);
    };
  }, [matrixClient, processInvites, pathname]);

  // #8314 — On cold reload the backend guest list often arrives before the Matrix initial
  // sync completes, so the listener effect's processInvites runs are gated out by
  // isInitialSyncComplete(). Re-run once the shell is ready AND whenever the backend guest
  // list changes, so the invite appears immediately instead of waiting for the next sync.
  useEffect(() => {
    if (!isMatrixReady) return;
    void processInvites();
  }, [isMatrixReady, contextGuestMeetings, processInvites]);
};

export default useMeetingSpaceInviteNotifications;
